// Time conversion constants
const DEFAULT_DURATION = 30;
const DEFAULT_TIME_UNIT = 'minutes';
const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 3600;
const SECONDS_IN_DAY = 86400;
const SECONDS_IN_WEEK = 604800;
const SECONDS_IN_MONTH = 2592000; // 30 days
const SECONDS_IN_YEAR = 31536000;  // 365 days
const PADDING_LENGTH = 2;
const PADDING_CHAR = '0';

export const TIME_CONSTANTS = {
  DEFAULT_DURATION,
  DEFAULT_TIME_UNIT,
  SECONDS_IN_MINUTE,
  SECONDS_IN_HOUR,
  SECONDS_IN_DAY,
  SECONDS_IN_WEEK,
  SECONDS_IN_MONTH,
  SECONDS_IN_YEAR
};

// Time unit conversions
export const TIME_UNIT_CONVERSIONS = {
  minutes: SECONDS_IN_MINUTE,
  hours: SECONDS_IN_HOUR,
  days: SECONDS_IN_DAY,
  weeks: SECONDS_IN_WEEK,
  months: SECONDS_IN_MONTH,
  years: SECONDS_IN_YEAR
};

// Helper to format time components
const padTime = (value) => value.toString().padStart(PADDING_LENGTH, PADDING_CHAR);

const formatHMS = (hours, minutes, seconds) => 
  `${padTime(hours)}:${padTime(minutes)}:${padTime(seconds)}`;

// Format time units based on duration - uses object parameter to reduce complexity
const formatYears = ({ years, months, days, hours, minutes, seconds }) =>
  `${years}y ${months}m ${days}d ${formatHMS(hours, minutes, seconds)}`;

const formatMonths = ({ months, days, hours, minutes, seconds }) =>
  `${months}m ${days}d ${formatHMS(hours, minutes, seconds)}`;

const formatWeeks = ({ weeks, days, hours, minutes, seconds }) =>
  `${weeks}w ${days}d ${formatHMS(hours, minutes, seconds)}`;

const formatDays = ({ days, hours, minutes, seconds }) =>
  `${days}d ${formatHMS(hours, minutes, seconds)}`;

const formatHours = ({ hours, minutes, seconds }) =>
  `${hours}:${padTime(minutes)}:${padTime(seconds)}`;

const formatMinutes = ({ minutes, seconds }) =>
  `${minutes}:${padTime(seconds)}`;

// Format time remaining to appropriate unit with early returns to reduce nesting
export const formatTimeRemaining = (seconds) => {
  const years = Math.floor(seconds / SECONDS_IN_YEAR);
  const remainingAfterYears = seconds % SECONDS_IN_YEAR;
  const months = Math.floor(remainingAfterYears / SECONDS_IN_MONTH);
  const remainingAfterMonths = remainingAfterYears % SECONDS_IN_MONTH;
  const weeks = Math.floor(remainingAfterMonths / SECONDS_IN_WEEK);
  const remainingAfterWeeks = remainingAfterMonths % SECONDS_IN_WEEK;
  const days = Math.floor(remainingAfterWeeks / SECONDS_IN_DAY);
  const remainingAfterDays = remainingAfterWeeks % SECONDS_IN_DAY;
  const hours = Math.floor(remainingAfterDays / SECONDS_IN_HOUR);
  const remainingAfterHours = remainingAfterDays % SECONDS_IN_HOUR;
  const minutes = Math.floor(remainingAfterHours / SECONDS_IN_MINUTE);
  const secs = remainingAfterHours % SECONDS_IN_MINUTE;

  // Create time object for consistent parameter passing
  const timeUnits = { years, months, weeks, days, hours, minutes, seconds: secs };

  // Early returns to reduce nesting
  if (years > 0) return formatYears(timeUnits);
  if (months > 0) return formatMonths(timeUnits);
  if (weeks > 0) return formatWeeks(timeUnits);
  if (days > 0) return formatDays(timeUnits);
  if (hours > 0) return formatHours(timeUnits);
  return formatMinutes(timeUnits);
};

// Convert duration to seconds based on unit
export const durationToSeconds = (duration, unit) => {
  return duration * TIME_UNIT_CONVERSIONS[unit];
};
